import React, { useState } from 'react'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyBtn } from '~/components/ui/MyBtn'
import { message, Popover } from 'antd'
import { MyDivide } from '~/components/ui/MyDivide'
import { xhrDownload, drawImageMark, graphQLErrorMessage } from '~/utils'
import { useToWatermarkAi } from '~/hooks/ai/useToWatermarkAi'
import { useAtom } from 'jotai'
import { detailSwiperIndexAtom } from './atom'
import { PostTypes } from '~/graphqls/types'

import styles from './AiItemTool.module.css'
import { useStores } from '~/hooks/useStores'
import { useValidateIntergal } from '~/hooks/useValidateIntergal'
import { observer } from 'mobx-react-lite'
import { useMutation } from '@apollo/client'
import { DOWNLOAD_IMAGE } from '~/graphqls/gqls'
import { downloadImage, downloadImageVariables } from '~/graphqls/types'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import classNames from 'classnames'

const AiItemTool = ({
  post,
  className,
}: {
  post: PostTypes
  className?: string
}) => {
  const [visible, setVisible] = useState(false)
  const [swiperindex] = useAtom(detailSwiperIndexAtom)
  const { store } = useStores()
  const { validateIsLogin } = useValidateAuth()
  const { IsNotEnough } = useValidateIntergal()
  const [loading, setLoading] = useState(false)
  const [downloadAction] = useMutation<downloadImage, downloadImageVariables>(
    DOWNLOAD_IMAGE
  )
  const { toWatermarkAiImage } = useToWatermarkAi()

  // 校验登录
  const validLogin = () => {
    if (!validateIsLogin()) {
      // 未登录，先关掉弹窗
      setVisible(false)
      return false
    } else {
      return true
    }
  }

  const onDownload = (url?: string) => {
    if (!validateIsLogin()) return

    xhrDownload(url ?? post?.imageList?.[swiperindex]?.imageUrl!, 'AI创作')
  }

  // 有水印下载
  const onDownloadMark = async () => {
    if (!validLogin()) return

    if (loading) {
      return
    }

    setLoading(true)
    try {
      const watermarkUrl = await toWatermarkAiImage(
        post?.imageList?.[swiperindex]?.imageUrl || post.coverImage?.imageUrl!
      )
      if (!watermarkUrl) {
        return
      }
      const imgUrl = await drawImageMark({
        imageUrl: watermarkUrl,
        nickname: post?.user?.nickname,
      })

      onDownload(imgUrl)
    } catch (e) {
      console.log(e)
    }
    setTimeout(() => {
      setLoading(false)
      setVisible(false)
    }, 300)
  }

  // 积分下载, 创作者本人下载不会扣积分
  const onPayIntergal = async () => {
    if (!validLogin()) return
    if (loading) return
    setLoading(true)
    try {
      const res = await downloadAction({
        variables: {
          param: {
            imageId: post?.imageList?.[swiperindex]?.id,
            postId: post.id,
            postScope: post.postScope,
          },
        },
      })
      if (res?.data?.squareModule?.url) {
        const newUrl = await toWatermarkAiImage(res?.data?.squareModule?.url)
        if (!newUrl) {
          return
        }
        onDownload(newUrl)
      }
    } catch (err) {
      message.destroy()
      if (IsNotEnough(err)) {
        message.error('积分不足，下载失败')
      } else {
        message.error(graphQLErrorMessage(err) || '下载失败，请重试~')
      }
    } finally {
      setLoading(false)
      setVisible(false)
    }
  }

  const isSelf =
    post?.user?.uid && post?.user?.uid === store.userinfo?.base?.uid

  return (
    <div className={classNames(className)}>
      <Popover
        open={visible}
        placement="bottom"
        onOpenChange={open => {
          setVisible(open)
        }}
        overlayClassName={styles.overlayClass}
        content={
          <div
            className="w-[339px] text-[14px] leading-6 text-neutral-500 font-semibol "
            style={{ opacity: loading ? 0.5 : 1 }}
          >
            <div className="flex items-center justify-between text-neutral-500">
              <span>下载图片（有水印）</span>
              <MyBtn
                type="default"
                size="small"
                className={styles.smallBtn}
                $dark
                onClick={() => onDownloadMark()}
              >
                下载
              </MyBtn>
            </div>
            {post?.canDownSourceImage && (
              <>
                <MyDivide dark space={16} />

                <div className="flex items-start justify-between text-neutral-500">
                  <span>下载原图（无用户昵称水印）</span>
                  {isSelf ? (
                    <MyBtn
                      type="default"
                      size="small"
                      className={styles.smallBtn}
                      $dark
                      onClick={() => onPayIntergal()}
                    >
                      下载
                    </MyBtn>
                  ) : (
                    <MyBtn
                      type="primary"
                      size="small"
                      className="w-[56px]"
                      $dark
                      onClick={() => onPayIntergal()}
                    >
                      2积分
                    </MyBtn>
                  )}
                </div>
                {!isSelf && (
                  <div className=" font-normal text-neutral-400  mt-3">
                    <div>1.下载创作者发布的AI创作原图每次需要消耗2积分。</div>
                    <div>
                      2.下载的原图仅限个人学习研究使用，禁止商用，如需商用请联系创作者获得授权。
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        }
      >
        <MyBtn $dark className="!px-2">
          <div className="flex items-center">
            <MyIcon icon="file-download" className="text-[20px] text-inherit" />
          </div>
        </MyBtn>
      </Popover>
    </div>
  )
}

export default observer(AiItemTool)
